import { useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const getVal = (x, ...keys) => {
  for (const k of keys)
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  tableWrap: {
    overflowX: "auto",
    maxHeight: 420,
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 850 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function DayBookPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ fromDate: today(), toDate: today() });

  const load = async () => {
    const res = await client.get(
      `/finance/day-book?fromDate=${filter.fromDate}&toDate=${filter.toDate}`,
    );
    setRows(res.data || []);
  };

  const totalDebit = rows.reduce(
    (s, r) => s + num(getVal(r, "Debit", "debit")),
    0,
  );

  const totalCredit = rows.reduce(
    (s, r) => s + num(getVal(r, "Credit", "credit")),
    0,
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>
            <i className="bi bi-calendar2-check me-2"></i>
            Day Book
          </div>
          <div style={styles.sub}>
            Daily voucher register with debit and credit totals
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <Field label="From Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={load}
              >
                <i className="bi bi-search me-1"></i>
                Load
              </button>
            </div>
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Records: <strong>{rows.length}</strong>
            </span>
            <span>
              Dr: <strong>{totalDebit.toFixed(2)}</strong> | Cr:{" "}
              <strong>{totalCredit.toFixed(2)}</strong>
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Description",
                    "Account",
                    "Debit",
                    "Credit",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Debit", "Credit"].includes(h) ? "text-end" : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No records
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const date = getVal(r, "EntryDate", "entryDate");
                    const debit = num(getVal(r, "Debit", "debit"));
                    const credit = num(getVal(r, "Credit", "credit"));

                    return (
                      <tr key={i}>
                        <td style={styles.td}>
                          {date
                            ? new Date(date).toLocaleDateString("en-IN")
                            : ""}
                        </td>
                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {getVal(r, "VoucherNo", "voucherNo")}
                        </td>
                        <td style={styles.td}>
                          {getVal(r, "Description", "description")}
                        </td>
                        <td style={styles.td}>
                          {getVal(r, "AccountName", "accountName")}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {debit > 0 ? debit.toFixed(2) : ""}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {credit > 0 ? credit.toFixed(2) : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8f9fa", fontWeight: 700 }}>
                    <td colSpan="4" className="text-end" style={styles.td}>
                      Total
                    </td>
                    <td className="text-end" style={styles.td}>
                      {totalDebit.toFixed(2)}
                    </td>
                    <td className="text-end" style={styles.td}>
                      {totalCredit.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
